import { useEffect, useState, type KeyboardEvent } from "react";

import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";
import type { LoomAuthoringCommand, LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";
import {
  createDefaultLoomJunctionFairingIntent,
  LOOM_JUNCTION_FAIRING_G1_TANGENT,
  LOOM_JUNCTION_FAIRING_G2_CURVATURE,
  LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M,
  loomJunctionFairingFootprintIssue,
  loomJunctionFairingRequestedContinuity,
  loomJunctionFairingStationExtentM,
  replaceLoomJunctionFairingRequestedContinuity,
  replaceLoomJunctionFairingStation,
  type LoomJunctionFairingIntent,
  type LoomJunctionFairingStation,
} from "./loomJunctionFairingIntent";
import {
  loomJunctionFairingFootprintIntentSignature,
  loomJunctionFairingPeriodicReach,
} from "./loomJunctionFairingFootprintPreview";

type WingNode = Extract<LoomSceneNode, { type: "primitive" }>;
type ScalarKey = Exclude<keyof LoomJunctionFairingStation, "stationId">;

interface LoomJunctionFairingEditorProps {
  readonly document: LoomAuthoringDocument;
  readonly mode?: "full" | "summary";
  readonly node: WingNode;
  readonly onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  readonly onDraftChange?: (draft: LoomJunctionFairingDraft) => void;
  readonly onEdit?: () => void;
}

export interface LoomJunctionFairingDraft {
  readonly baseRevision: number;
  readonly intent: LoomJunctionFairingIntent;
  readonly nodeId: string;
}

const ADVANCED_FIELD_ROWS: readonly {
  key: ScalarKey;
  label: string;
  max: number;
  min: number;
  step: number | "any";
  unit: string;
}[] = [
  { key: "junctionAngleDeg", label: "Angle around join", min: 0, max: 359.99, step: 1, unit: "°" },
  { key: "parentATangentScale", label: "Wing departure", min: 0.05, max: 2, step: "any", unit: "×" },
  { key: "parentBTangentScale", label: "Fuselage arrival", min: 0.05, max: 2, step: "any", unit: "×" },
] as const;

const STATION_LABELS: Readonly<Record<string, string>> = {
  aft: "Aft",
  forward: "Forward",
  lower: "Lower",
  port: "Port",
  starboard: "Starboard",
  upper: "Upper",
};

const FAIRING_TANGENT_SCALE_MIN = 0.05;
const FAIRING_TANGENT_SCALE_MAX = 2;

function biasCapacity(fullness: number): number {
  return Math.min(
    fullness - FAIRING_TANGENT_SCALE_MIN,
    FAIRING_TANGENT_SCALE_MAX - fullness,
  );
}

function nudgeRange(
  event: KeyboardEvent<HTMLInputElement>, value: number, step: number,
  minimum: number, maximum: number, change: (next: number) => void,
) {
  const direction = event.key === "ArrowRight" || event.key === "ArrowUp" ? 1
    : event.key === "ArrowLeft" || event.key === "ArrowDown" ? -1 : 0;
  if (!direction) return;
  event.preventDefault();
  change(Math.max(minimum, Math.min(maximum, Number((value + direction * step).toFixed(12)))));
}

function biasText(bias: number): string {
  if (Math.abs(bias) < 0.005) return "Even between wing and fuselage";
  return `${bias < 0 ? "Wing" : "Fuselage"}-led ${Math.round(Math.abs(bias) * 100)} percent`;
}

function stationFullness(station: LoomJunctionFairingStation): number {
  return (station.parentATangentScale + station.parentBTangentScale) / 2;
}

function stationBias(station: LoomJunctionFairingStation): number {
  const fullness = stationFullness(station);
  const maximumDelta = biasCapacity(fullness);
  return maximumDelta <= Number.EPSILON
    ? 0
    : Math.max(-1, Math.min(1,
      (station.parentBTangentScale - station.parentATangentScale) / (2 * maximumDelta)));
}

function shapePatch(fullness: number, bias: number) {
  const boundedFullness = Math.max(FAIRING_TANGENT_SCALE_MIN,
    Math.min(FAIRING_TANGENT_SCALE_MAX, fullness));
  const boundedBias = Math.max(-1, Math.min(1, bias));
  const maximumDelta = biasCapacity(boundedFullness);
  return {
    parentATangentScale: boundedFullness - boundedBias * maximumDelta,
    parentBTangentScale: boundedFullness + boundedBias * maximumDelta,
  };
}

function parentSignature(intent: LoomJunctionFairingIntent, key: "parentAOffsetM" | "parentBOffsetM"): string {
  return intent.construction.stations.map((station) => (
    `${station.stationId}:${station.junctionAngleDeg.toFixed(2)}:${station[key].toFixed(6)}`
  )).join("|");
}

function footprintPlot(intent: LoomJunctionFairingIntent, key: "parentAOffsetM" | "parentBOffsetM") {
  const sign = key === "parentAOffsetM" ? -1 : 1;
  return Array.from({ length: 73 }, (_unused, index) => {
    const angle = index * 5;
    const x = 16 + angle / 360 * 208;
    const reach = loomJunctionFairingPeriodicReach(intent.construction.stations, key, angle);
    return { angle, reach, x, y: 62 + sign * reach / LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M * 42 };
  });
}

function path(points: readonly { x: number; y: number }[]): string {
  return points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");
}

export function LoomJunctionFairingEditor({ document, mode = "full", node, onCommand, onDraftChange, onEdit }: LoomJunctionFairingEditorProps) {
  if (node.definition.primitive !== "wing") return null;
  const definition = node.definition;
  const fairing = definition.junctionFairing;
  const [draftFairing, setDraftFairing] = useState(fairing);
  const [stationId, setStationId] = useState(fairing?.construction.stations[0]?.stationId ?? "forward");
  useEffect(() => { setDraftFairing(fairing); }, [fairing, node.id]);
  useEffect(() => {
    if (fairing && !fairing.construction.stations.some((station) => station.stationId === stationId)) {
      setStationId(fairing.construction.stations[0]!.stationId);
    }
  }, [fairing, stationId]);

  const replace = (junctionFairing: typeof fairing) => onCommand({
    schema: "loom-authoring-command/v1",
    commandId: createLoomCommandId("junction-fairing", document.revision),
    expectedRevision: document.revision,
    kind: "primitive.replace",
    nodeId: node.id,
    definition: junctionFairing === undefined
      ? (({ junctionFairing: _removed, ...remainder }) => remainder)(definition)
      : { ...definition, junctionFairing },
  }, { origin: "gui.scene", subjectIds: [node.id, fairing?.transitionId ?? "main-wing-body-fairing"] });

  if (!fairing) return <section className="loom-junction-fairing-editor"
    data-state="absent" data-testid="loom-junction-fairing-editor">
    <header><span>Junction fairing</span><h3>Wing–body transition</h3></header>
    <p>Shape an owned transition around the join. The accepted aircraft stays visible while it rebuilds.</p>
    <button onClick={() => replace(createDefaultLoomJunctionFairingIntent())}
      type="button">Add junction fairing</button>
    <small>A dorsal can use the same angular law; accepting it into the aircraft still requires a fin–body trim and weld adapter.</small>
  </section>;

  if (mode === "summary") return <section className="loom-junction-fairing-editor"
    data-state="linked" data-testid="loom-junction-fairing-editor">
    <header><span>Linked fairing</span><h3>Wing–body fairing</h3></header>
    <p>Owned by {node.label}. Select it to edit its seam law independently.</p>
    <button onClick={onEdit} type="button">Edit fairing</button>
  </section>;

  const draft = draftFairing ?? fairing;
  const dirty = JSON.stringify(draft) !== JSON.stringify(fairing);
  const footprintIssue = loomJunctionFairingFootprintIssue(draft);
  const continuity = loomJunctionFairingRequestedContinuity(draft);
  const publishDraft = (next: LoomJunctionFairingIntent) => {
    setDraftFairing(next);
    onDraftChange?.({ baseRevision: document.revision, intent: next, nodeId: node.id });
  };
  const changeContinuity = (parent: "parentA" | "parentB", value: typeof continuity.parentA) => {
    publishDraft(replaceLoomJunctionFairingRequestedContinuity(draft, {
      ...continuity,
      [parent]: value,
    }));
  };
  const station = draft.construction.stations.find((candidate) => candidate.stationId === stationId)
    ?? draft.construction.stations[0]!;
  const changeStation = (key: ScalarKey, rawValue: string) => {
    const value = Number(rawValue);
    if (!Number.isFinite(value) || value === station[key]) return;
    publishDraft(replaceLoomJunctionFairingStation(draft, station.stationId, { [key]: value }));
  };
  const wingPlot = footprintPlot(draft, "parentAOffsetM");
  const fuselagePlot = footprintPlot(draft, "parentBOffsetM");
  const footprintBand = `${path(wingPlot)} ${path([...fuselagePlot].reverse()).replace(/^M/, "L")} Z`;
  const stationLabel = STATION_LABELS[station.stationId] ?? station.stationId;
  const fullness = stationFullness(station);
  const bias = stationBias(station);
  const biasAvailable = biasCapacity(fullness) > Number.EPSILON;
  const biasHelpId = `${node.id}-fairing-bias-help`;
  const changeFullness = (value: number) => publishDraft(replaceLoomJunctionFairingStation(
    draft, station.stationId, shapePatch(value, bias),
  ));
  const changeBias = (value: number) => publishDraft(replaceLoomJunctionFairingStation(
    draft, station.stationId, shapePatch(fullness, value),
  ));
  return <section className="loom-junction-fairing-editor" data-state="authored"
    data-draft-state={dirty ? "changed" : "current"}
    data-testid="loom-junction-fairing-editor">
    <header><span>Junction fairing</span><h3>Wing–body transition</h3></header>
    <p>Place its two contact lines, shape the transition between them, then choose how it meets each parent.</p>
    <fieldset aria-label="Fairing footprint" className="loom-task-fairing-footprint">
      <legend>1 · Contact footprint</legend>
      <div className="loom-task-fairing-map" data-footprint-signature={loomJunctionFairingFootprintIntentSignature(draft)}
        data-fuselage-reach-signature={parentSignature(draft, "parentBOffsetM")}
        data-testid="loom-fairing-footprint-map"
        data-wing-reach-signature={parentSignature(draft, "parentAOffsetM")}>
        <svg aria-label="Topographic fairing footprint projection" role="img" viewBox="0 0 240 124">
          <line className="loom-fairing-map-axis" x1="16" x2="224" y1="62" y2="62" />
          {[0, 90, 180, 270, 360].map((angle) => <line className="loom-fairing-map-guide"
            key={angle} x1={16 + angle / 360 * 208} x2={16 + angle / 360 * 208} y1="15" y2="109" />)}
          <path className="loom-fairing-map-band" d={footprintBand} />
          <path className="loom-fairing-map-wing" d={path(wingPlot)} />
          <path className="loom-fairing-map-fuselage" d={path(fuselagePlot)} />
          {draft.construction.stations.flatMap((candidate) => {
            const x = 16 + candidate.junctionAngleDeg / 360 * 208;
            const selected = candidate.stationId === station.stationId;
            return [
              <circle className="loom-fairing-map-wing" cx={x}
                cy={62 - candidate.parentAOffsetM / LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M * 42}
                data-selected={String(selected)} key={`${candidate.stationId}:wing`} r={selected ? 3.2 : 2.2} />,
              <circle className="loom-fairing-map-fuselage" cx={x}
                cy={62 + candidate.parentBOffsetM / LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M * 42}
                data-selected={String(selected)} key={`${candidate.stationId}:fuselage`} r={selected ? 3.2 : 2.2} />,
            ];
          })}
          <text x="18" y="12">Wing reach</text><text x="18" y="121">Fuselage reach</text>
        </svg>
        <div aria-label="Footprint station" className="loom-task-fairing-stations">
          {draft.construction.stations.map((candidate) => {
            const label = STATION_LABELS[candidate.stationId] ?? candidate.stationId;
            return <button aria-label={`${label} footprint station`}
              aria-pressed={candidate.stationId === station.stationId} key={candidate.stationId}
              onClick={() => setStationId(candidate.stationId)} type="button">{label}</button>;
          })}
        </div>
      </div>
      <div className="loom-task-fairing-parent-controls">
        {([
          ["parentAOffsetM", "Wing"],
          ["parentBOffsetM", "Fuselage"],
        ] as const).map(([key, parent]) => <label key={key}>
          <span>{parent} reach <small>m</small></span>
          <span className="loom-task-fairing-reach-inputs">
            <input aria-label={`${stationLabel} ${parent.toLowerCase()} reach slider`}
              max={LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M} min={0}
              onChange={(event) => changeStation(key, event.target.value)} step={0.005}
              type="range" value={station[key]} />
            <input aria-invalid={footprintIssue ? true : undefined}
              aria-label={`${stationLabel} ${parent.toLowerCase()} reach`}
              max={LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M} min={0}
              onChange={(event) => changeStation(key, event.target.value)} step={0.005}
              type="number" value={station[key]} />
          </span>
        </label>)}
        <output>Total reach {loomJunctionFairingStationExtentM(station).toFixed(3)} m</output>
      </div>
    </fieldset>
    <small>Projected guides update immediately against the admitted wing and fuselage parent definitions. They are visual-only and may cross the retained fairing; no fairing geometry has been rebuilt. The last accepted aircraft stays visible until Apply passes geometry QC.</small>
    <fieldset aria-label="Fairing section shape" className="loom-task-fairing-shape">
      <legend>2 · Section shape</legend>
      <p>Adjust the selected direction. Fullness changes the boundary-tangent magnitude and cross-section form; bias assigns more of it to one parent.</p>
      <label className="loom-task-fairing-shape-control">
        <span>Fullness <output>{fullness.toFixed(3)}×</output></span>
        <input aria-label={`${stationLabel} fairing fullness`} max={FAIRING_TANGENT_SCALE_MAX}
          min={FAIRING_TANGENT_SCALE_MIN}
          onChange={(event) => changeFullness(Number(event.target.value))}
          onKeyDown={(event) => nudgeRange(event, fullness, 0.05,
            FAIRING_TANGENT_SCALE_MIN, FAIRING_TANGENT_SCALE_MAX, changeFullness)}
          step="any" type="range" value={fullness} />
      </label>
      <label className="loom-task-fairing-shape-control">
        <span>Parent bias <output>{biasText(bias)}</output></span>
        <input aria-describedby={biasHelpId} aria-label={`${stationLabel} fairing parent bias`}
          aria-valuetext={biasText(bias)} disabled={!biasAvailable} max={1} min={-1}
          onChange={(event) => changeBias(Number(event.target.value))}
          onKeyDown={(event) => nudgeRange(event, bias, 0.05, -1, 1, changeBias)}
          step="any" type="range" value={bias} />
        <span aria-hidden="true" className="loom-task-fairing-bias-labels">
          <small>Wing-led</small><small>Even</small><small>Fuselage-led</small>
        </span>
        <small id={biasHelpId}>{biasAvailable
          ? "The colored surface ticks show the visual-only departure guide before Apply."
          : "Bias is unavailable at minimum or maximum fullness because no redistribution remains."}</small>
      </label>
      <output className="loom-task-fairing-shape-result">
        {stationLabel}: wing departure {station.parentATangentScale.toFixed(2)}× · fuselage arrival {station.parentBTangentScale.toFixed(2)}×
      </output>
    </fieldset>
    <fieldset aria-label="Continuity" className="loom-task-fairing-continuity">
      <legend>3 · Attach to parents</legend>
      <fieldset aria-label="Wing contact continuity">
        <legend>Wing contact</legend>
        <label><input checked={continuity.parentA === LOOM_JUNCTION_FAIRING_G1_TANGENT}
          name={`wing-contact-continuity-${node.id}`} onChange={() => changeContinuity(
            "parentA", LOOM_JUNCTION_FAIRING_G1_TANGENT,
          )} type="radio" />Tangent (G1)</label>
        <label><input checked={continuity.parentA === LOOM_JUNCTION_FAIRING_G2_CURVATURE}
          disabled name={`wing-contact-continuity-${node.id}`} readOnly type="radio" />Curvature (G2)</label>
        <small>Curvature (G2) is unavailable at the wing contact because this ruled, blunt-trailing-edge wing does not have a certified second-order surface chart.</small>
      </fieldset>
      <fieldset aria-label="Fuselage contact continuity">
        <legend>Fuselage contact</legend>
        <label><input checked={continuity.parentB === LOOM_JUNCTION_FAIRING_G1_TANGENT}
          name={`fuselage-contact-continuity-${node.id}`} onChange={() => changeContinuity(
            "parentB", LOOM_JUNCTION_FAIRING_G1_TANGENT,
          )} type="radio" />Tangent (G1)</label>
        <label><input checked={continuity.parentB === LOOM_JUNCTION_FAIRING_G2_CURVATURE}
          name={`fuselage-contact-continuity-${node.id}`} onChange={() => changeContinuity(
            "parentB", LOOM_JUNCTION_FAIRING_G2_CURVATURE,
          )} type="radio" />Curvature (G2)</label>
      </fieldset>
      <small>Continuity is requested independently at each parent contact. Loom will not silently substitute a lower order.</small>
    </fieldset>
    <details className="loom-task-fairing-advanced">
      <summary>Advanced station values</summary>
      <small>In Loom coordinates, 0° is aft (+x) and 180° is forward (−x). Exact server QC may reject extreme or unbalanced values.</small>
      <label className="loom-task-field"><span>Angular station</span>
        <select aria-label="Angular station" onChange={(event) => setStationId(event.target.value)}
          value={station.stationId}>
          {draft.construction.stations.map((candidate) => <option key={candidate.stationId}
            value={candidate.stationId}>{STATION_LABELS[candidate.stationId] ?? candidate.stationId}</option>)}
        </select>
      </label>
      <fieldset><legend>{STATION_LABELS[station.stationId] ?? station.stationId}</legend>
        {ADVANCED_FIELD_ROWS.map((field) => <label className="loom-task-fairing-field" key={field.key}>
          <span>{field.label}<small>{field.unit}</small></span>
          <input aria-label={field.label} max={field.max} min={field.min}
            onChange={(event) => changeStation(field.key, event.target.value)}
            step={field.step} type="number" value={station[field.key]} />
        </label>)}
      </fieldset>
    </details>
    <div className="loom-task-fairing-actions" data-step="4">
      <strong>4 · Apply shape</strong>
      <button disabled={!dirty || Boolean(footprintIssue)} onClick={() => replace(draft)}
        type="button">Apply fairing shape</button>
      <button disabled={!dirty} onClick={() => publishDraft(fairing)}
        type="button">Reset draft</button>
      <span aria-live="polite">{footprintIssue ?? (dirty
        ? "Fairing changes not applied" : "Draft matches the authored definition")}</span>
    </div>
    <small>Flow features may propose these values later; they do not own or certify the geometry.</small>
    <button onClick={() => replace(undefined)} type="button">Remove junction fairing</button>
  </section>;
}
